import clsx from 'clsx'
import {cloneDeep, find, get, isEmpty, omit, set, size} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {ConfirmActionModal} from '../../../../../_gori/components'
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  FORMAT_DATE,
  MOBILE_BREAKPOINT,
  OPTION_COUNTRIES,
  TABLE_KEY_COLUMN,
  TABLET_BREAKPOINT,
  TABLET_MIN_WIDTH,
} from '../../../../../_gori/constants'
import {ROUTES} from '../../../../../_gori/constants'
import {
  KTSVG,
  convertUserTimeZone,
  getShipmentFees,
  setLoadingActionTable,
  useDisabled,
} from '../../../../../_gori/helpers'
import {blockPopUp} from '../../../../../_gori/helpers/BrowserHelpers'
import {TableSkeleton} from '../../../../../_gori/helpers/components'
import {useMeter} from '../../../../../_gori/helpers/components/MeterProvider'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import {
  Button,
  ButtonBack,
  CarrierBadge,
  DataTab,
  DropdownButton,
  Table,
  TableBody,
  TableHeader,
  TableTabs,
  TableWrapper,
  ValidationErrorModal,
} from '../../../../../_gori/partials/widgets'
import {useA1BatchesProvider} from '../../../../../_gori/providers/A1BatchesProvider'
import HttpStatusCode from '../../../../api/HttpStatusCode'
import {
  A1BatchesDetailFilter,
  EditOrderInfo,
  JourneyInfoCard,
  JourneyInformationModal,
  JourneyStatusCard,
  TicketForumModal,
  ReasonModal,
} from '../../../a1'
import {useAuth} from '../../../auth'
import {ButtonConfirmProgress, CreateAllLabelsModal} from '../../../batches'
import {AddressView, TrackingNumbersDetail} from '../../../orders'
import {OPTIONS_CONTENT_TYPE, OrderStatus} from '../../../orders/core/_const'
import {isCreatedWithinLast30Days} from '../../../orders/core/_helpers'
import A1_CONSTANTS from '../../core/_const'
import A1BatchService from '../../core/_requests'
import {getCSSVariableValue} from '../../../../../_gori/assets/ts/_utils'
import {BatchScanResult} from '../../modals/batches-page/scan/BatchScanResult'
import {useA1OrderFilter} from '../../hooks'
import {TooltipModal} from '../../modals/batches-detail/TooltipModal'
import moment from 'moment'
import {OrderTrackingNumberCell} from '../../../orders/components/order-child/OrderTrackingNumberCell'
import {CUSTOM_ISSUES_TAB_KEY} from '../../consts'
import {RemoveA1Batch} from './RemoveA1Batch'
import {RenameA1Batch} from './RenameA1Batch'
import useWindowDimensions from '../../../../../_gori/hooks/useWindowDimensions'

const SkeletonA1BatchDetail = () => (
  <>
    <div className='d-flex flex-wrap mb-10'>
      <div className='col-12 col-xxl-6'>
        <div className='card me-xxl-4 h-100'>
          <div className='card-header border-0 fw-bolder d-flex justify-content-between align-items-center'>
            <span className='fs-1 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
          </div>
          <div className='card-body bg-light rounded-bottom d-flex flex-column flex-md-row'>
            <div className='col-12 col-md-6'>
              <div className='d-flex pe-md-6 text-primary fs-5 fs-xxl-7 text-center fw-bolder'>
                <div className='col-4'>
                  <span className='fs-1 col-9 placeholder placeholder-lg rounded-2 bg-secondary' />
                </div>
                <div className='col-4'>
                  <span className='fs-1 col-9 placeholder placeholder-lg rounded-2 bg-secondary' />
                </div>
                <div className='col-4'>
                  <span className='fs-1 col-9 placeholder placeholder-lg rounded-2 bg-secondary' />
                </div>
              </div>
              <div className='d-flex flex-column min-h-150px pe-md-6'></div>
            </div>
            <span className='border border-secondary my-8 my-md-n8' />
            <div className='col-12 col-md-6 ps-6 fs-5 fs-xxl-7 fw-bold'>
              <div className='col-12 d-flex border-bottom border-secondary min-h-50px' />
              <div className='col-12 d-flex border-bottom border-secondary min-h-50px' />
              <div className='col-12 d-flex border-bottom border-secondary min-h-50px' />
            </div>
          </div>
        </div>
      </div>
      <div className='col-12 col-xxl-6'>
        <div className='card mt-4 mt-xxl-0 ms-xxl-4 h-100'>
          <div className='card-header border-0 d-flex justify-content-between align-items-center fw-bolder'>
            <span className='fs-1 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
          </div>
          <div className='card-body d-flex flex-column scroll-x pb-6'>
            <div className='d-flex text-center mb-6'>
              {Object.entries(omit(A1_CONSTANTS.OPTIONS_JOURNEY_STATUS, 'N_A')).map(
                ([key, status]) => (
                  <div className='col-2 min-w-100px position-relative' key={key}>
                    <div className='w-100 border border-2 border-secondary position-absolute top-50 start-50 translate-middle' />
                    <KTSVG
                      path={`/media/gori/a1/checkbox_unchecked.svg`}
                      className='svg-icon-1 position-absolute top-50 start-50 translate-middle'
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    <TableWrapper>
      <TableBody>
        <div className='d-flex justify-content-end my-5'>
          <span className='btn col-2 placeholder placeholder-lg rounded-2 bg-secondary' />
        </div>
        <TableSkeleton countRow={8} />
      </TableBody>
    </TableWrapper>
  </>
)

const A1BatchesDetailPage: React.FC = () => {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const {setMeterChange} = useMeter()
  const [searchParams, setSearchParams] = useSearchParams()
  const {setPageDisabled} = useDisabled()
  const {statesGlobal} = useStatesGlobal()
  const carriers = statesGlobal.carriers
  const {currentUser, loadingSwitch} = useAuth()
  const {a1Batch, setA1Batch, ticketForum, setTicketForum} = useA1BatchesProvider()
  const {newCancelToken, isCancel} = useCancelToken()
  const [selectedListId, setSelectedListId] = useState<any>([])
  const [selectedMaster, setSelectedMaster] = useState<boolean>(false)
  const [tableData, setTableData] = useState<any>([])
  const [pagination, setPagination] = useState<any>({})
  const [statistics, setStatistics] = useState<any>([])
  const [metadata, setMetadata] = useState<any>([])
  const [validationErrors, setValidationErrors] = useState<any>()
  const [loading, setLoading] = useState<{
    first: boolean
    getA1Batch: boolean
    removeBatch: boolean
    printAllLabel: boolean
    sendToPartner: boolean
  }>({
    first: true,
    getA1Batch: false,
    removeBatch: false,
    printAllLabel: false,
    sendToPartner: false,
  })
  const [showModal, setShowModal] = useState<{
    removeBatch: boolean
    journeyInfo: boolean
    createAllLabel: boolean
    editOrderInfo: boolean
    confirmAction: boolean
    batchScan: boolean
    reason: boolean
    trackingNumbersDetail: boolean
  }>({
    removeBatch: false,
    journeyInfo: false,
    createAllLabel: false,
    editOrderInfo: false,
    confirmAction: false,
    batchScan: false,
    reason: false,
    trackingNumbersDetail: false,
  })
  const [dataModal, setDataModal] = useState<{
    createAllLabel: boolean
    editOrderInfo: any
    confirmAction: {
      title?: string | null
      message?: any
      callback: any
    }
    reasonData: {type: string; reason: string; first_release_status: string} | null
    trackingNumbersDetail: any
  }>({
    createAllLabel: false,
    editOrderInfo: {},
    confirmAction: {title: null, message: null, callback: null},
    reasonData: null,
    trackingNumbersDetail: [],
  })
  const [dataTabs, setDataTabs] = useState<DataTab[]>([])

  const {state} = useLocation()
  const {filters} = useA1OrderFilter()
  const {width} = useWindowDimensions()

  const isNAStatus = useMemo(
    () => a1Batch?.journey_status === A1_CONSTANTS.OPTIONS_JOURNEY_STATUS.N_A.value[0],
    [a1Batch?.journey_status]
  )

  const isCustomIssuesTab = useMemo(
    () => searchParams.get('order_tab') === CUSTOM_ISSUES_TAB_KEY,
    [searchParams]
  )

  const setTable = useCallback(async () => {
    try {
      setPageDisabled(true)

      const config = {
        params: {
          batch_id: id,
          page: searchParams.get('page') || DEFAULT_PAGE,
          page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
          first_held: searchParams.get('first_held') || null,
          status: searchParams.get('status') || null,
          sort_by: searchParams.get('sort_by') || null,
          sort_direction: searchParams.get('sort_direction') || null,
          incomplete: searchParams.get('incomplete') || null,
          order_tab: searchParams.get('order_tab') || undefined,
          ...filters,
        },
        cancelToken: newCancelToken(),
      }
      const response = await A1BatchService.getA1BatchOrders(id, config)
      if (response) {
        let newOrder = Array.from(response.orders, (order: any) => {
          return {
            ...order,
            canCheck: true,
          }
        })
        setTableData(newOrder)
        setDataTabs(response.statistics)
        setStatistics(response.statistics)
        setMetadata(response.metadata)
        setPagination(response.pagination)
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setPageDisabled(false)
      setLoading((prev) => ({...prev, first: false}))
    }
  }, [filters, id, isCancel, newCancelToken, searchParams, setPageDisabled])

  const getA1Batch = useCallback(async () => {
    try {
      setLoading((prev) => ({...prev, getA1Batch: true}))
      const {
        a1_batch,
        from,
        orders_done,
        orders_readied,
        tracking_status,
        order_partially_done,
        order_count = 0,
        issue_resolved = false,
      } = await A1BatchService.getDetail(id, {
        cancelToken: newCancelToken(),
      })
      if (a1_batch) {
        setA1Batch({
          ...a1_batch,
          from,
          orders_done,
          orders_readied,
          tracking_status,
          order_partially_done,
          order_count,
          issue_resolved,
        })
      }
    } catch (error: any) {
      if (isCancel(error)) return
      if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        navigate(-1)
      }
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, getA1Batch: false}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancel, newCancelToken])

  useEffect(() => {
    if (loadingSwitch) return
    setTable()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSwitch, setTable])

  useEffect(() => {
    if (loadingSwitch) return
    getA1Batch()

    return () => {
      setA1Batch(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSwitch])

  const baseColor = getCSSVariableValue('--bs-primary')

  const isHoldOrRejected = useCallback((status) => {
    return [
      A1_CONSTANTS.OPTIONS_RELEASE_STATUS.HOLD.value,
      A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value,
    ].includes(status)
  }, [])

  const columns = useMemo(() => {
    const showEditOrderInfo = (child, row, showRequired = true) => {
      return (
        <div
          className='cursor-pointer'
          onClick={() => {
            setDataModal((prev) => ({...prev, editOrderInfo: row.original}))
            setShowModal((prev) => ({...prev, editOrderInfo: true}))
          }}
        >
          {showRequired ? (
            <div className='d-flex text-danger justify-content-center'>
              <KTSVG
                path='/media/gori/settings/errorsPreset.svg'
                className='svg-icon-3 text-danger'
              />
              <span className='ms-2 text-decoration-underline text-uppercase'>
                {intl.formatMessage({id: 'REQUIRED'})}
              </span>
            </div>
          ) : (
            <span className='text-hover-primary'>{child}</span>
          )}
        </div>
      )
    }

    const _columns = [
      {
        id: TABLE_KEY_COLUMN.CAN_CHECK,
        Header: intl.formatMessage({id: 'ID'}),
        accessor: TABLE_KEY_COLUMN.CAN_CHECK,
      },
      {
        id: 'created_at',
        Header: intl.formatMessage({id: 'DATE'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(
            convertUserTimeZone(
              row.original?.created_at,
              currentUser,
              process.env.REACT_APP_DATE_FORMAT
            ),
            row,
            false
          )
        },
      },
      {
        id: 'code',
        Header: 'BAE ID',
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => showEditOrderInfo(row.original?.bae_id, row, false),
      },
      {
        id: 'customer',
        Header: intl.formatMessage({id: 'CUSTOMER'}),
        headerClassName: 'min-w-200px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(
            <AddressView data={row.original.to} locationClassNames='text-gray-600' />,
            row,
            false
          )
        },
      },
      {
        id: 'tracking_number',
        Header: intl.formatMessage({id: 'TRACKING_NUMBER'}),
        headerClassName: 'min-w-150px d-flex flex-column align-items-center pe-6',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          if (isEmpty(carriers)) return null
          let service = carriers[row.original.carrier + '_' + row.original.service] ?? []

          const isMultiParcel = row.original?.latest_shipment?.tracking_numbers.length > 1
          const trackingNumbers = row.original?.latest_shipment?.tracking_numbers || null

          return (
            <>
              <OrderTrackingNumberCell
                onClickTrackingNumbersDetail={() => {
                  setShowModal((prev) => ({...prev, trackingNumbersDetail: true}))
                  setDataModal((prev) => ({
                    ...prev,
                    trackingNumbersDetail: row,
                  }))
                }}
                service={service}
                trackingNumbers={trackingNumbers}
                isMultiParcel={isMultiParcel}
              />
              {showEditOrderInfo(
                <>
                  {!isEmpty(row.original.error) && (
                    <div className='row'>
                      <span className='text-danger'>{row.original?.error?.message}</span>
                      <span className='text-danger'>{row.original?.error?.Description}</span>
                      {row.original?.error?.fields &&
                        Object.entries(row.original.error.fields).map(
                          ([key, value]: [any, any]) => {
                            return (
                              <span key={key} className='text-danger'>
                                - {key} {value}
                              </span>
                            )
                          }
                        )}
                    </div>
                  )}
                </>,
                row,
                false
              )}
            </>
          )
        },
      },
      {
        id: 'rate',
        Header: intl.formatMessage({id: 'RATE'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          let sum = 0
          let service = carriers?.[row.original.carrier + '_' + row.original.service] ?? []

          if (row.original?.fees) {
            sum = getShipmentFees(row.original?.fees)
          } else if (row.original?.estimated_rate) {
            sum = row.original?.estimated_rate
          }
          const isCheapest = row.original?.is_cheapest_rate ?? false
          return showEditOrderInfo(
            row.original?.carrier && (
              <CarrierBadge
                service={service}
                isCheapest={isCheapest}
                {...((row.original?.fees || row.original?.estimated_rate) && {
                  cost: sum,
                })}
              />
            ),
            row,
            false
          )
        },
      },
      {
        id: 'hawb',
        Header: 'HAWB',
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(row?.original?.a1?.hawb || null, row, false)
        },
      },
      {
        id: 'container_id',
        Header: intl.formatMessage({id: 'CONTAINER_ID'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(row?.original?.a1?.container_id || null, row, false)
        },
      },
      {
        id: 'name',
        Header: intl.formatMessage({id: 'NAME'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            (row.original?.expand ? row.original?.items : [row.original?.items[0]]).map(
              (item: any) => {
                return (
                  item?.name && (
                    <div className='d-flex'>
                      <span className='mw-200px text-truncate'>{item?.name}</span>
                    </div>
                  )
                )
              }
            ),
            row,
            isEmpty(row.original?.items) || row.original?.items?.some((item) => isEmpty(item?.name))
          ),
      },
      {
        id: 'status',
        Header: intl.formatMessage({id: 'CUSTOM_STATUS'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          const foundRelease =
            find(Object.values(A1_CONSTANTS.A1_ORDERS_OPTION_RELEASE_STATUS), {
              value: row?.original?.a1.status,
            }) || null

          return showEditOrderInfo(
            foundRelease && intl.formatMessage({id: foundRelease?.label}),
            row,
            false
          )
        },
      },
      {
        id: 'content',
        Header: intl.formatMessage({id: 'CONTENT'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            !isEmpty(row.original.items) &&
              (row.original?.expand ? row.original?.items : [row.original?.items[0]]).map(
                (item: any) => {
                  const content = find(OPTIONS_CONTENT_TYPE, {value: item?.a1?.content})
                  return (
                    content && (
                      <div className='d-flex'>
                        <span className='mw-200px text-truncate'>
                          {intl.formatMessage({id: content?.label})}
                        </span>
                      </div>
                    )
                  )
                }
              ),
            row,
            isEmpty(row.original?.items) ||
              row.original?.items?.some((item) => isEmpty(item?.a1?.content))
          ),
      },
      {
        id: 'description',
        Header: intl.formatMessage({id: 'DESCRIPTION'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            (row.original?.expand ? row.original?.items : [row.original?.items[0]]).map(
              (item: any) => {
                return (
                  item?.a1?.description && (
                    <div className='d-flex'>
                      <span className='mw-200px text-truncate'>{item?.a1?.description}</span>
                    </div>
                  )
                )
              }
            ),
            row,
            isEmpty(row.original?.items) ||
              row.original?.items?.some((item) => isEmpty(item?.a1?.description))
          ),
      },
      {
        id: 'hts_code',
        Header: intl.formatMessage({id: 'HTS_CODE'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            !isEmpty(row.original.items) &&
              (row.original?.expand ? row.original?.items : [row.original?.items[0]]).map(
                (item: any) => {
                  return (
                    item?.a1?.hs_tariff_number && (
                      <div className='d-flex'>
                        <span className='mw-200px text-truncate'>{item?.a1?.hs_tariff_number}</span>
                      </div>
                    )
                  )
                }
              ),
            row,
            isEmpty(row.original?.items) ||
              row.original?.items?.some((item) => isEmpty(item?.a1?.hs_tariff_number))
          ),
      },
      {
        id: 'country',
        Header: intl.formatMessage({id: 'COUNTRY_OF_ORIGIN'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            !isEmpty(row.original.items) &&
              (row.original?.expand ? row.original?.items : [row.original?.items[0]]).map(
                (item: any) => {
                  const country = find(OPTION_COUNTRIES, {value: item?.a1?.country})
                  return (
                    country && (
                      <div className='d-flex'>
                        <span className='mw-200px text-truncate'>{country?.label}</span>
                      </div>
                    )
                  )
                }
              ),
            row,
            isEmpty(row.original?.items) ||
              row.original?.items?.some((item) => isEmpty(item?.a1?.country))
          ),
      },
      {
        id: 'quantity',
        Header: intl.formatMessage({id: 'QUANTITY'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            !isEmpty(row.original.items) &&
              (row.original?.expand ? row.original?.items : [row.original?.items[0]]).map(
                (item: any) => {
                  const quantity = item?.a1?.quantity?.toString()

                  return (
                    !!quantity && <div className='d-flex justify-content-center'>{quantity}</div>
                  )
                }
              ),
            row,
            isEmpty(row.original?.items) ||
              row.original?.items?.some((item) => isEmpty(item?.a1?.quantity?.toString()))
          ),
      },
      {
        id: 'value',
        Header: `${intl.formatMessage({id: 'VALUES'})} ($)`,
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-center text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            !isEmpty(row.original.items) &&
              (row.original?.expand ? row.original?.items : [row.original?.items[0]]).map(
                (item: any) => {
                  const formatNumber = (number = 0) => {
                    return intl.formatNumber(Number(number), {
                      style: 'decimal',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }

                  return (
                    item?.a1?.value && (
                      <div className='d-flex justify-content-center'>
                        {formatNumber(item?.a1?.value)}
                      </div>
                    )
                  )
                }
              ),
            row,
            isEmpty(row.original?.items) ||
              row.original?.items?.some((item) => !Number(item?.a1?.value))
          ),
      },
      {
        id: 'actions_header',
        Header: '',
        headerClassName: 'text-center',
        cellClassName: 'text-center',
        Cell: ({row}: {row: any}) => (
          <div
            className={clsx('btn btn-advanced-tertiary fw-bold cursor-pointer text-nowrap p-1', {
              'd-none': size(row.original.items) <= 1,
              'min-w-150px': size(row.original.items) > 1,
            })}
            onClick={() => {
              setTableData(
                setLoadingActionTable(tableData, [row.original.id], !row.original.expand, 'expand')
              )
            }}
          >
            {intl.formatMessage({id: row.original.expand ? 'COLLAPSE' : 'VIEW_MORE_ITEMS'})}
          </div>
        ),
        visible: !isCustomIssuesTab,
      },
      {
        id: 'type',
        Header: intl.formatMessage({id: 'TYPE'}),
        headerClassName: 'min-w-175px',
        cellClassName: 'text-wrap text-dark',
        Cell: ({row}: {row: any}) => {
          if (!isHoldOrRejected(row.original.a1?.status)) return null

          const type =
            row.original.a1?.status === A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value
              ? row?.original?.a1?.rejected_type
              : row?.original?.a1?.hold_type

          return type ? intl.formatMessage({id: type.toUpperCase()}) : null
        },
        visible: isCustomIssuesTab,
      },
      {
        id: 'reason',
        Header: intl.formatMessage({id: 'REASON'}),
        headerClassName: 'min-w-100px text-center',
        cellClassName: 'text-wrap text-dark text-center',
        Cell: ({row}: {row: any}) => {
          if (!isHoldOrRejected(row.original.a1?.status)) return null

          const type =
            row.original.a1?.status === A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value
              ? row?.original?.a1?.rejected_type
              : row?.original?.a1?.hold_type

          const reason =
            row.original.a1?.status === A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value
              ? row?.original?.a1?.rejected_reason
              : row?.original?.a1?.hold_reason
          return (
            <TooltipModal
              message={
                <div
                  className='cursor-pointer'
                  onClick={() => {
                    setDataModal((prev) => ({
                      ...prev,
                      reasonData: {
                        type: type ? intl.formatMessage({id: type.toUpperCase()}) : '',
                        reason,
                        first_release_status: row?.original?.a1?.first_release_status,
                      },
                    }))
                    setShowModal((prev) => ({...prev, reason: true}))
                  }}
                >
                  <div className='d-flex align-items-center text-danger'>
                    <KTSVG path='/media/gori/invalid.svg' className='svg-icon-3 text-danger' />
                    <span className='ms-2 text-decoration-underline text-uppercase'>
                      {intl.formatMessage({id: 'VIEW'})}
                    </span>
                  </div>
                </div>
              }
              messageTooltip={row?.original?.hold_reason || row?.original?.rejection_reason}
            />
          )
        },
        visible: isCustomIssuesTab,
      },
    ]
    return _columns.filter((col) => col && col.visible !== false)
  }, [intl, isCustomIssuesTab, currentUser, carriers, tableData, isHoldOrRejected])

  const detailMenu = () => {
    const isDisabled = a1Batch?.status !== A1_CONSTANTS.A1_BATCH_STATUS.OPEN

    const detailMenuOptions = [
      {
        children: (
          <RenameA1Batch
            id={a1Batch?.id}
            name={a1Batch?.name}
            disabled={isDisabled}
            onSuccess={(newName: string) => setA1Batch((prev) => ({...prev, name: newName}))}
            onError={(error) => setValidationErrors(error)}
          />
        ),
      },
      {
        children: (
          <RemoveA1Batch
            id={a1Batch?.id}
            disabled={isDisabled}
            onSuccess={() => navigate(ROUTES.A1.BASE)}
            onError={(error) => setValidationErrors(error)}
          />
        ),
      },
    ]

    return (
      <div className='ms-4 batch-detail-menu h-auto'>
        <DropdownButton
          list={detailMenuOptions}
          title={intl.formatMessage({id: 'ACTIONS'})}
          className={clsx('btn btn-advanced-tertiary btn-sm fw-bolder ps-3 pe-1 py-0 rounded-2')}
        />
      </div>
    )
  }
  // END: Title Page

  const orderIds = useMemo(() => {
    return {
      createAllLabel:
        find(metadata, {value: OrderStatus.OPEN})?.orders?.map((item) => item.id) ?? [],
      printAllLabel:
        find(metadata, {value: OrderStatus.SHIPPED})
          ?.orders?.filter((item) => isCreatedWithinLast30Days(item?.created_at))
          ?.map((item) => item.id) ?? [],
    }
  }, [metadata])

  const handleCreateAllLabels = useCallback(() => {
    if (!a1Batch?.orders_readied) {
      return toast.error(intl.formatMessage({id: 'ALL_REQUIRED_FIELDS_OF_ORDER_MUST_BE_COMPLETED'}))
    }
    setDataModal((prev) => ({...prev, createAllLabel: orderIds.createAllLabel}))
    setShowModal((prev) => ({...prev, createAllLabel: true}))
  }, [a1Batch?.orders_readied, intl, orderIds.createAllLabel])

  const handleCloseCreateAllModal = useCallback(() => {
    setMeterChange(true)
    setShowModal((prev) => ({...prev, createAllLabel: false}))
    setTable()
    getA1Batch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkValidEPU = () => {
    if (currentUser?.settings?.yamato_account) {
      return true
    }

    if (!a1Batch?.estimated_pickup_at) {
      toast.error(intl.formatMessage({id: 'JOURNEY_INFO_FILL_IN'}))
      return false
    }

    const epu = moment(moment.utc(a1Batch.estimated_pickup_at).toDate()).local()
    const min = moment().local().add(1, 'hour')

    if (epu.isBefore(min)) {
      const message = intl.formatMessage(
        {id: 'PICK_UP_TIME_MUST_BE_1H_ONWARDS'},
        {
          input: intl.formatMessage({id: 'PICK_UP_DATE'}),
          min: min.format(FORMAT_DATE.DATE_TIME_TIMEZONE_FULL),
        }
      )
      toast.error(message)

      return false
    }
    return true
  }

  const handleSendToPartner = useCallback(
    async ({hasPass = false}) => {
      if (!checkValidEPU()) {
        return
      }
      if (!hasPass) {
        setDataModal((prev) => ({
          ...prev,
          confirmAction: {
            title: intl.formatMessage({id: 'SEND_TO_PARTNER'}),
            callback: () => handleSendToPartner({hasPass: true}),
          },
        }))
        return setShowModal((prev) => ({...prev, confirmAction: true}))
      }

      const config = {cancelToken: newCancelToken()}

      try {
        setLoading((prev) => ({...prev, sendToPartner: true}))
        const response = await A1BatchService.sendToPartner(a1Batch?.id, config)
        if (response) {
          await getA1Batch()
          toast.success(intl.formatMessage({id: response?.message}))
        }
      } catch (error: any) {
        if (isCancel(error)) return
        const errRes = cloneDeep(error?.response)
        set(errRes, 'data.error.message', intl.formatMessage({id: errRes?.data?.error?.message}))
        setValidationErrors(errRes)
      } finally {
        setLoading((prev) => ({...prev, sendToPartner: false}))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [a1Batch?.id, getA1Batch, intl, isCancel, newCancelToken, a1Batch?.estimated_pickup_at]
  )

  const handlePrintAllLabels = useCallback(async () => {
    const config = {cancelToken: newCancelToken()}
    const payload = {order_ids: orderIds.printAllLabel, a1_batch: a1Batch.name}

    try {
      setLoading((prev) => ({...prev, printAllLabel: true}))
      const response = await A1BatchService.printLabel(payload, config)
      if (response) {
        if (!response.label_url) {
          return toast.warning(
            intl.formatMessage({
              id: 'PRINT_LABELS_REFUNDED',
            })
          )
        }
        if (blockPopUp(response.label_url)) {
          toast.warning(
            intl.formatMessage({
              id: 'POP_UP_IS_BLOCKED',
            })
          )
        }
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, printAllLabel: false}))
    }
  }, [a1Batch?.name, intl, isCancel, newCancelToken, orderIds.printAllLabel])

  const handleUpdateJourneySuccess = useCallback(async () => {
    setShowModal((prev) => ({...prev, journeyInfo: false}))
    toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
    await getA1Batch()
  }, [getA1Batch, intl])

  const handleCloseEditOrderInfo = useCallback(() => {
    setDataModal((prev) => ({...prev, editOrderInfo: {}}))
    setShowModal((prev) => ({...prev, editOrderInfo: false}))
  }, [])

  const handleUpdateOrderInfoSuccess = useCallback(async () => {
    handleCloseEditOrderInfo()
    await setTable()
    await getA1Batch()
  }, [getA1Batch, handleCloseEditOrderInfo, setTable])

  const handleConfirmAction = useCallback(async () => {
    setShowModal((prev) => ({...prev, confirmAction: false}))
    await dataModal.confirmAction.callback()
    await setDataModal((prev) => ({
      ...prev,
      confirmAction: {
        callback: null,
      },
    }))
  }, [dataModal.confirmAction])

  const handleCloseConfirmAction = useCallback(() => {
    setDataModal((prev) => ({
      ...prev,
      confirmAction: {
        title: null,
        message: null,
        callback: null,
      },
    }))
    setShowModal((prev) => ({...prev, confirmAction: false}))
  }, [])

  const handleTicketForum = useCallback(() => {
    const activeTabName = Object.entries(a1Batch?.ticket_unread_count ?? {}).find(
      (item) => (item as [string, number])[1] > 0
    )?.[0]
    searchParams.set('page', DEFAULT_PAGE)
    if (activeTabName) {
      searchParams.set('sender', activeTabName)
    }
    searchParams.delete('page_size')
    searchParams.set('modal', 'true')
    setSearchParams(searchParams)
    document.body.style.overflow = 'hidden'
    setTicketForum((prev) => ({...prev, showList: true}))
  }, [searchParams, setSearchParams, setTicketForum, a1Batch?.ticket_unread_count])

  useEffect(() => {
    if (!a1Batch?.id) return

    if (get(state, 'ticketModal')) {
      handleTicketForum()
    }
  }, [handleTicketForum, state, a1Batch?.id])

  const handleCloseTicketForum = () => {
    getA1Batch()
    document.body.style.overflow = 'auto'
    setTicketForum((prev) => ({...prev, showList: false}))
  }

  return loading.first || isEmpty(a1Batch) ? (
    <SkeletonA1BatchDetail />
  ) : (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <BatchScanResult
        show={showModal.batchScan}
        handleClose={() => {
          setShowModal((prev) => ({...prev, batchScan: false}))
        }}
        setShow={(isShow: boolean) => setShowModal((prev) => ({...prev, batchScan: isShow}))}
        type={'existing'}
        handleSuccess={() => setTable()}
        batchInfo={{
          title: a1Batch.name,
          batchId: a1Batch.id,
          from: a1Batch.from,
          order_count: a1Batch?.order_count,
        }}
      />
      {showModal.trackingNumbersDetail && (
        <TrackingNumbersDetail
          show={showModal.trackingNumbersDetail}
          data={dataModal.trackingNumbersDetail}
          handleClose={() => setShowModal((prev) => ({...prev, trackingNumbersDetail: false}))}
        />
      )}
      {showModal.confirmAction && (
        <ConfirmActionModal
          show={showModal.confirmAction}
          classNameDialog='modal-size-lg'
          title={dataModal?.confirmAction?.title}
          message={
            dataModal?.confirmAction?.message || (
              <span className='fw-bold text-center'>
                <p>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_PROCEED'})}</p>
                <p>
                  {intl.formatMessage({
                    id: 'THIS_ACTION_CANNOT_BE_UNDONE_PLEASE_REVIEW_YOUR_DECISION_CAREFULLY_BEFORE_PROCEEDING',
                  })}
                </p>
              </span>
            )
          }
          messagePrimary={intl.formatMessage({id: 'CONFIRM'})}
          messageSecondary={intl.formatMessage({id: 'CANCEL'})}
          handlePrimaryAction={handleConfirmAction}
          handleSecondaryAction={handleCloseConfirmAction}
          handleClose={handleCloseConfirmAction}
        />
      )}
      {showModal.journeyInfo && (
        <JourneyInformationModal
          show={showModal.journeyInfo}
          handleClose={() => setShowModal((prev) => ({...prev, journeyInfo: false}))}
          handleSuccess={handleUpdateJourneySuccess}
        />
      )}
      {showModal.createAllLabel && (
        <CreateAllLabelsModal
          show={showModal.createAllLabel}
          orderIds={dataModal.createAllLabel}
          handleClose={() => handleCloseCreateAllModal()}
        />
      )}
      {showModal.editOrderInfo && (
        <EditOrderInfo
          show={showModal.editOrderInfo}
          data={dataModal.editOrderInfo}
          handleClose={handleCloseEditOrderInfo}
          handleSuccess={handleUpdateOrderInfoSuccess}
        />
      )}
      {showModal.reason && (
        <ReasonModal
          show={showModal.reason}
          handleClose={() => setShowModal((prev) => ({...prev, reason: false}))}
          data={dataModal.reasonData}
        />
      )}
      <TicketForumModal show={ticketForum?.showList} handleClose={handleCloseTicketForum} />
      <div className={clsx({'cursor-no-drop': loading.getA1Batch})}>
        <div className={clsx({'pe-none opacity-75': loading.getA1Batch})}>
          <div className='d-flex flex-wrap justify-content-between mb-4'>
            <div className='d-flex'>
              <div className='d-flex align-items-center'>
                <ButtonBack className='a1-batches-detail-title__back-button' />
                <button
                  type='button'
                  className='a1-batches-detail-title__back-arrow'
                  aria-label='Go back'
                  onClick={() => navigate(-1)}
                >
                  <KTSVG path='/media/gori/left_arrow.svg' />
                </button>
              </div>
              {a1Batch?.created_at && (
                <div className='d-flex'>
                  <div className='fw-bolder'>
                    <span className='a1-batches-detail-title__name'>{a1Batch?.name}</span>
                    <div className='fs-6 mt-2 fw-bold'>
                      <span className='a1-batches-detail-title__date me-1'>
                        {convertUserTimeZone(
                          a1Batch.created_at,
                          currentUser,
                          process.env.REACT_APP_DATE_FORMAT
                        )}
                      </span>{' '}
                      <span className='a1-batches-detail-title__time'>
                        {convertUserTimeZone(
                          a1Batch.created_at,
                          currentUser,
                          process.env.REACT_APP_TIME_FORMAT
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className={clsx('d-flex ms-auto align-items-center', {
                'mt-20px': width < TABLET_BREAKPOINT,
                'w-100 justify-content-between': width <= TABLET_MIN_WIDTH,
                'flex-wrap gap-3': width <= MOBILE_BREAKPOINT,
              })}
            >
              {a1Batch?.status === A1_CONSTANTS.A1_BATCH_STATUS.OPEN && (
                <Button
                  className='btn btn-sm btn-advanced-secondary me-2 w-100'
                  classNameDiv={clsx('me-2', {'w-100': width <= MOBILE_BREAKPOINT})}
                  label={intl.formatMessage({id: 'SCAN_TO_ADD_SHIPMENTS'})}
                  event={() => setShowModal((prev) => ({...prev, batchScan: true}))}
                  icon={<KTSVG path='/media/gori/a1/scan.svg' className='svg-icon-3 icon-scan' />}
                />
              )}

              {!a1Batch?.orders_done && isNAStatus && (
                <>
                  {!a1Batch?.flights?.length ? (
                    <Button
                      className='btn btn-sm btn-advanced-primary'
                      label={intl.formatMessage({id: 'UPDATE_BATCH_STATUS'})}
                      event={() => setShowModal((prev) => ({...prev, journeyInfo: true}))}
                    />
                  ) : (
                    <ButtonConfirmProgress
                      className='btn btn-sm text-white bg-image-gori user-select-none'
                      label={intl.formatMessage({id: 'CREATE_ALL_LABELS'})}
                      callBack={handleCreateAllLabels}
                    />
                  )}
                </>
              )}
              {detailMenu()}
              {a1Batch?.orders_done && a1Batch?.orders_readied && (
                <div className='d-flex align-items-center'>
                  {isNAStatus && (
                    <Button
                      className='btn btn-sm btn-advanced-primary me-2'
                      label={intl.formatMessage({id: 'SEND_TO_PARTNER'})}
                      loading={loading.sendToPartner}
                      loadingText={intl.formatMessage({id: 'SEND_TO_PARTNER'})}
                      disabled={loading.sendToPartner}
                      event={handleSendToPartner}
                    />
                  )}
                  <Button
                    className={clsx('btn btn-sm btn-light-blue border border-blue', {
                      disabled: isEmpty(orderIds.printAllLabel),
                    })}
                    label={intl.formatMessage({id: 'PRINT_ALL_LABELS'})}
                    loadingText={intl.formatMessage({id: 'PRINT_ALL_LABELS'})}
                    loading={loading.printAllLabel}
                    event={handlePrintAllLabels}
                  />
                </div>
              )}
              <div className='d-flex justify-content-center align-items-center'>
                <div
                  className={clsx(
                    'ms-2 h-36px bg-white rounded-2 btn btn-sm bg-hover-light-primary d-flex justify-content-center align-items-center'
                  )}
                  onClick={handleTicketForum}
                >
                  <KTSVG
                    path={
                      a1Batch?.ticket_comment_unread
                        ? '/media/gori/a1/unread-message.svg'
                        : '/media/gori/a1/message.svg'
                    }
                    small={false}
                    style={{color: baseColor}}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='d-flex flex-wrap mb-10'>
            <div className='col-12 col-xxl-6'>
              <JourneyInfoCard
                handleEditJourneyInfo={() => setShowModal((prev) => ({...prev, journeyInfo: true}))}
              />
            </div>
            <div className='col-12 col-xxl-6'>
              <JourneyStatusCard />
            </div>
          </div>
          <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
            <TableTabs
              disableTabAll
              dataTabs={dataTabs}
              keyCheckActive='order_tab'
              children={
                <TableWrapper className='rounded-top-left-0-dt'>
                  <div className='table-content--inset-inner h-100'>
                    <TableHeader className='min-h-1px d-flex flex-column min-h-1'>
                      <A1BatchesDetailFilter
                        selectedList={{
                          selected: selectedListId,
                          selectedMaster: selectedMaster,
                          total: pagination?.total,
                        }}
                        statistics={statistics}
                        reloadTable={() => {
                          setMeterChange(true)
                          setTable()
                          getA1Batch()
                        }}
                      />
                    </TableHeader>
                    <TableBody height={'calc(100vh - 300px)'}>
                      <Table
                        columns={columns}
                        data={tableData}
                        pagination={pagination}
                        tbodyClass='text-gray-600 fw-bold'
                        usePagination
                        useCheckBox
                        callBackSetData={(selected) => {
                          setSelectedMaster(selected?.length === pagination?.page_size)
                          setSelectedListId(selected)
                        }}
                      />
                    </TableBody>
                  </div>
                </TableWrapper>
              }
            />
          </CSSTransition>
        </div>
      </div>
    </>
  )
}

export {A1BatchesDetailPage}
